import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Share2, Sparkles } from 'lucide-react';
import { Drawer } from 'vaul';
import { buildWidgetOptions } from './widgetOptions';
import { FavoriteDrawingToolbar } from './FavoriteDrawingToolbar';
import { useTradingViewWidget } from './useTradingViewWidget';
import type { TradingViewDatafeed } from '../types/datafeed-api';
import type { ChartLayout } from '../types/settings';
import type { PlusClickParams, SettingsAdapter } from '../types/charting-library';
import type { ChartSymbol, PriceAlarm, WatchlistFlagColor } from '../types/api';

const TIMEFRAMES = [
  { label: '1m', value: '1' },
  { label: '5m', value: '5' },
  { label: '15m', value: '15' },
  { label: '30m', value: '30' },
  { label: '1H', value: '60' },
  { label: '4H', value: '240' },
  { label: '1D', value: '1D' },
  { label: '1W', value: '1W' },
  { label: '9W', value: '9W' },
] as const;

type TimeframeOption = (typeof TIMEFRAMES)[number];

function triggerSelectionHaptic() {
  navigator.vibrate?.(8);
}

interface TradingViewChartProps {
  datafeed: TradingViewDatafeed;
  symbol: string;
  interval: string;
  layout: ChartLayout | null;
  settingsAdapter: SettingsAdapter;
  watchlistSymbolNames: ReadonlySet<string>;
  flaggedWatchlistSymbolNames: ReadonlySet<string>;
  flaggedWatchlistSymbolColors: Readonly<Record<string, WatchlistFlagColor>>;
  activeWatchlistSymbols: ChartSymbol[];
  alarms: PriceAlarm[];
  layoutMode?: 'mobile' | 'desktop';
  enableCustomIndicators?: boolean;
  enableNineWeekInterval?: boolean;
  onAutoSave(layout: ChartLayout): void;
  onFlush(): void;
  onSymbolSelectorOpen?(): void;
  onSymbolChange?(symbol: string): void;
  onIntervalChange?(interval: string): void;
  onAddToWatchlist?(symbol: string): void;
  onRemoveFromWatchlist?(symbol: string): void;
  onToggleWatchlistShortcut?(symbol: string): Promise<boolean> | boolean;
  onToggleFlagShortcut?(symbol: string): Promise<boolean> | boolean;
  onSetFlagColor?(symbol: string, flagColor: WatchlistFlagColor | null): Promise<boolean> | boolean;
  onDeleteAlarm?(id: string): void;
  onPlusClick?(params: PlusClickParams): void;
  onMarketContextOpen?(): void;
  draftAlarmPrice?: number | null;
  onDraftAlarmPriceChange?(price: number): void;
}

export interface TradingViewChartHandle {
  saveLayout(): Promise<ChartLayout>;
}

export const TradingViewChart = forwardRef<TradingViewChartHandle, TradingViewChartProps>(function TradingViewChart({ datafeed, symbol, interval, layout, settingsAdapter, watchlistSymbolNames, flaggedWatchlistSymbolNames, flaggedWatchlistSymbolColors, activeWatchlistSymbols, alarms, layoutMode = 'mobile', enableCustomIndicators = false, enableNineWeekInterval = false, onAutoSave, onFlush, onSymbolSelectorOpen, onSymbolChange, onIntervalChange, onAddToWatchlist, onRemoveFromWatchlist, onToggleWatchlistShortcut, onToggleFlagShortcut, onSetFlagColor, onDeleteAlarm, onPlusClick, onMarketContextOpen, draftAlarmPrice, onDraftAlarmPriceChange }, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [widgetVersion, setWidgetVersion] = useState(0);
  const [timeframeSheetOpen, setTimeframeSheetOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const selectedWheelSymbol = activeWatchlistSymbols.find((item) => item.name === symbol);
  const wheelSymbols = selectedWheelSymbol
    ? activeWatchlistSymbols
    : [{ name: symbol, description: symbol, exchange: '', fullName: symbol, pricescale: 100, session: '24x7', type: 'crypto' as const }];

  const setChartContainer = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    setContainer(node);
  }, []);

  const timeframes = enableNineWeekInterval ? TIMEFRAMES : TIMEFRAMES.filter((timeframe) => timeframe.value !== '9W');

  const options = useMemo(() => (
    container
      ? buildWidgetOptions({ container, datafeed, symbol, interval, layout, settingsAdapter, layoutMode, enableCustomIndicators })
      : null
  ), [container, datafeed, enableCustomIndicators, interval, layout, layoutMode, settingsAdapter, symbol]);

  const widgetRef = useTradingViewWidget({
    options,
    onAutoSave,
    onFlush,
    onSymbolChange,
    onIntervalChange,
    onAddToWatchlist,
    onRemoveFromWatchlist,
    onToggleWatchlistShortcut,
    onToggleFlagShortcut,
    onSetFlagColor,
    onDeleteAlarm,
    onPlusClick,
    draftAlarmPrice,
    onDraftAlarmPriceChange,
    onReady: () => setWidgetVersion((value) => value + 1),
    watchlistSymbolNames,
    flaggedWatchlistSymbolNames,
    flaggedWatchlistSymbolColors,
    alarms,
  });

  useImperativeHandle(ref, () => ({
    saveLayout() {
      return new Promise<ChartLayout>((resolve, reject) => {
        const widget = widgetRef.current;
        if (!widget) {
          reject(new Error('Chart is not ready'));
          return;
        }
        widget.save(resolve);
      });
    },
  }), [widgetRef]);

  async function shareChartScreenshot() {
    const widget = widgetRef.current;
    if (!widget?.takeClientScreenshot || sharing) return;

    setSharing(true);
    try {
      const canvas = await widget.takeClientScreenshot();
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Screenshot export failed');

      const file = new File([blob], `${symbol}-${interval}.png`, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: `${symbol} ${interval}`, text: `${symbol} ${interval}` });
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('share chart screenshot:', err);
    } finally {
      setSharing(false);
    }
  }

  return (
    <div className={layoutMode === 'mobile' ? 'chart-container chart-container-with-toolbar' : 'chart-container'}>
      <div
        ref={setChartContainer}
        className="chart-widget-host"
      />
      {layoutMode === 'mobile' && (
        <>
          <FavoriteDrawingToolbar
            key={widgetVersion}
            widget={widgetRef.current}
            settingsAdapter={settingsAdapter}
            leadingControls={(
              <>
                <VerticalSymbolScroller
                  symbols={wheelSymbols}
                  selectedSymbol={symbol}
                  onOpenPicker={onSymbolSelectorOpen}
                  onSelect={(nextSymbol) => {
                    widgetRef.current?.setSymbol(nextSymbol, interval);
                    onSymbolChange?.(nextSymbol);
                  }}
                />
                <VerticalTimeframeScroller
                  interval={interval}
                  timeframes={timeframes}
                  onOpenPicker={() => setTimeframeSheetOpen(true)}
                  onSelect={(nextInterval) => {
                    widgetRef.current?.setSymbol(symbol, nextInterval);
                    onIntervalChange?.(nextInterval);
                  }}
                />
              </>
            )}
            trailingControls={(
              <>
                {onMarketContextOpen && (
                  <button
                    type="button"
                    className="chart-drawing-actions-bar__button"
                    aria-label="Open AI market context"
                    title="AI market context"
                    onClick={onMarketContextOpen}
                  >
                    <Sparkles aria-hidden="true" />
                  </button>
                )}
                <button
                  type="button"
                  className="chart-drawing-actions-bar__button"
                  aria-label="Share chart screenshot"
                  disabled={sharing}
                  onClick={() => void shareChartScreenshot()}
                >
                  <Share2 aria-hidden="true" />
                </button>
              </>
            )}
          />
          <TimeframeSheet
            open={timeframeSheetOpen}
            interval={interval}
            timeframes={timeframes}
            onOpenChange={setTimeframeSheetOpen}
            onSelect={(nextInterval) => {
              widgetRef.current?.setSymbol(symbol, nextInterval);
              onIntervalChange?.(nextInterval);
              setTimeframeSheetOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
});

function VerticalSymbolScroller({ symbols, selectedSymbol, onOpenPicker, onSelect }: {
  symbols: ChartSymbol[];
  selectedSymbol: string;
  onOpenPicker?(): void;
  onSelect(symbol: string): void;
}) {
  const selectedIndex = Math.max(0, symbols.findIndex((item) => item.name === selectedSymbol));
  return (
    <VerticalWheel
      className="chart-symbol-wheel"
      items={symbols.map((item) => ({ label: item.name, value: item.name }))}
      selectedIndex={selectedIndex}
      selectedValue={selectedSymbol}
      onOpenPicker={onOpenPicker}
      onSelect={onSelect}
    />
  );
}

function VerticalTimeframeScroller({ interval, timeframes, onOpenPicker, onSelect }: {
  interval: string;
  timeframes: readonly TimeframeOption[];
  onOpenPicker(): void;
  onSelect(interval: string): void;
}) {
  const selectedIndex = Math.max(0, timeframes.findIndex((item) => item.value === interval));
  return (
    <VerticalWheel
      className="chart-timeframe-wheel"
      items={timeframes}
      selectedIndex={selectedIndex}
      selectedValue={interval}
      onOpenPicker={onOpenPicker}
      onSelect={onSelect}
    />
  );
}

function VerticalWheel({ className, items, selectedIndex, selectedValue, onOpenPicker, onSelect, wrapPreview = false }: {
  className: string;
  items: readonly { label: string; value: string }[];
  selectedIndex: number;
  selectedValue: string;
  onOpenPicker?(): void;
  onSelect(value: string): void;
  wrapPreview?: boolean;
}) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const scrollTimerRef = useRef<number | null>(null);
  const hidePopupTimerRef = useRef<number | null>(null);
  const userScrollRef = useRef(false);
  const pointerActiveRef = useRef(false);
  const touchActiveRef = useRef(false);
  const pendingIndexRef = useRef(selectedIndex);
  const [popupOpen, setPopupOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(selectedIndex);
  const previewItems = [previewIndex - 1, previewIndex, previewIndex + 1].map((index) => {
    const wrappedIndex = wrapPreview && items.length > 0 ? (index + items.length) % items.length : index;
    return {
      item: items[wrappedIndex],
      index,
    };
  });

  useEffect(() => {
    const item = listRef.current?.querySelector<HTMLElement>(`[data-index="${selectedIndex}"]`);
    item?.scrollIntoView({ block: 'center' });
    pendingIndexRef.current = selectedIndex;
    setPreviewIndex(selectedIndex);
  }, [items.length, selectedIndex]);

  useEffect(() => () => {
    if (scrollTimerRef.current !== null) window.clearTimeout(scrollTimerRef.current);
    if (hidePopupTimerRef.current !== null) window.clearTimeout(hidePopupTimerRef.current);
  }, []);

  function beginUserScroll() {
    userScrollRef.current = true;
    if (hidePopupTimerRef.current !== null) window.clearTimeout(hidePopupTimerRef.current);
    setPopupOpen(true);
  }

  function commitPendingSelection() {
    const nextValue = items[pendingIndexRef.current]?.value;
    if (userScrollRef.current && nextValue && nextValue !== selectedValue) {
      triggerSelectionHaptic();
      onSelect(nextValue);
    }
    hidePopupTimerRef.current = window.setTimeout(() => {
      userScrollRef.current = false;
      setPopupOpen(false);
    }, 520);
  }

  function scheduleCommit(delay: number) {
    if (scrollTimerRef.current !== null) window.clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = window.setTimeout(() => {
      if (pointerActiveRef.current || touchActiveRef.current) return;
      commitPendingSelection();
    }, delay);
  }

  return (
    <div className={`chart-vertical-wheel ${className}`}>
      <div className="chart-vertical-wheel__popup" data-open={popupOpen} aria-hidden="true">
        {previewItems.map(({ item, index }) => (
          <div
            key={item?.value ?? `empty-${index}`}
            className="chart-vertical-wheel__popup-item"
            data-active={index === previewIndex}
            data-empty={!item}
          >
            {item && className.includes('symbol') ? <span className="chart-vertical-wheel__popup-icon">{item.label.slice(0, 1)}</span> : null}
            <span>{item?.label}</span>
          </div>
        ))}
      </div>
      <div
        ref={listRef}
        className="chart-vertical-wheel__list"
        aria-label="Drag to select"
        onPointerDown={(event) => {
          pointerActiveRef.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          beginUserScroll();
        }}
        onPointerUp={() => {
          pointerActiveRef.current = false;
          scheduleCommit(180);
        }}
        onPointerCancel={() => {
          if (!touchActiveRef.current) pointerActiveRef.current = false;
          scheduleCommit(180);
        }}
        onTouchStart={() => {
          touchActiveRef.current = true;
          beginUserScroll();
        }}
        onTouchMove={beginUserScroll}
        onTouchEnd={() => {
          touchActiveRef.current = false;
          pointerActiveRef.current = false;
          scheduleCommit(180);
        }}
        onTouchCancel={() => {
          touchActiveRef.current = false;
          pointerActiveRef.current = false;
          scheduleCommit(180);
        }}
        onWheel={beginUserScroll}
        onScroll={(event) => {
          if (hidePopupTimerRef.current !== null) window.clearTimeout(hidePopupTimerRef.current);
          const target = event.currentTarget;
          const targetCenter = target.getBoundingClientRect().top + target.clientHeight / 2;
          const rows = Array.from(target.querySelectorAll<HTMLElement>('[data-index]'));
          const nextIndex = rows.reduce((nearestIndex, row) => {
            const rowCenter = row.getBoundingClientRect().top + row.offsetHeight / 2;
            const nearestRow = rows[nearestIndex];
            if (!nearestRow) return Number(row.dataset.index ?? 0);
            const nearestCenter = nearestRow.getBoundingClientRect().top + nearestRow.offsetHeight / 2;
            return Math.abs(rowCenter - targetCenter) < Math.abs(nearestCenter - targetCenter)
              ? Number(row.dataset.index ?? nearestIndex)
              : nearestIndex;
          }, pendingIndexRef.current);
          pendingIndexRef.current = nextIndex;
          setPreviewIndex(nextIndex);
          if (userScrollRef.current) setPopupOpen(true);
          scheduleCommit(360);
        }}
      >
        {items.map((item, index) => (
          <button
            key={item.value}
            type="button"
            data-index={index}
            className={index === previewIndex ? 'chart-vertical-wheel__item active' : 'chart-vertical-wheel__item'}
            onClick={() => {
              if (item.value === selectedValue && onOpenPicker) {
                onOpenPicker();
                return;
              }
              pendingIndexRef.current = index;
              setPreviewIndex(index);
              triggerSelectionHaptic();
              onSelect(item.value);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function TimeframeSheet({ open, interval, timeframes, onOpenChange, onSelect }: {
  open: boolean;
  interval: string;
  timeframes: readonly TimeframeOption[];
  onOpenChange(open: boolean): void;
  onSelect(interval: string): void;
}) {
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="drawer-overlay" />
        <Drawer.Content className="drawer-content timeframe-sheet" aria-describedby={undefined} onOpenAutoFocus={(event) => event.preventDefault()}>
          <div className="sheet-handle" aria-hidden="true" />
          <div className="timeframe-sheet-title">Timeframe</div>
          <div className="timeframe-scroll" aria-label="Select timeframe">
            {timeframes.map((item) => (
              <button
                key={item.value}
                type="button"
                className={interval === item.value ? 'timeframe-item active' : 'timeframe-item'}
                onClick={() => onSelect(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
